import type { CivilAdviseWizardDraft } from '../types/civilAdviseCaseRequest';
import type { CivilAppellateWizardDraft } from '../types/civilAppellateCaseRequest';
import type { CivilLitigationWizardDraft } from '../types/civilLitigationCaseRequest';
import type { CriminalWizardDraft } from '../types/criminalCaseRequest';
import type { ExtraditionMlaWizardDraft } from '../types/extraditionMlaCaseRequest';
import { defaultHasActionInstitutedForSubCategory } from '../pages/wizard/civilLitigation/civilLitigationHelpers';

type SharedCategorizationSeed = {
  fileCategoryId: string;
  fileCategory: string;
  subFileCategoryId: string;
  subFileCategoryName: string;
  applicableAreaIds: string[];
  applicableAreas: string[];
  instituteType: '' | 'government' | 'corporation';
  corporateInstituteId: string;
  corporateInstituteName: string;
  corporateInstituteOther: string;
  corporateInstituteContactName: string;
  corporateInstitutePrefix: string;
  description: string;
  provinceId: string;
  provinceName: string;
  districtId: string;
  districtName: string;
  yourReferenceNumber: string;
  otherExternalReferences: string;
  caseNumber: string;
  hasConnectedMatters: '' | 'yes' | 'no';
  connectedCaseNumber: string;
  connectedAgdFileRef: string;
  urgencyLevelId: string;
  urgencyLevelName: string;
  deadline: string;
  urgencyJustification: string;
  deliveryMethodIds: string[];
  deliveryMethods: string[];
  deliveryOther: string;
  draftRequestId: string;
  referenceNo: string;
};

function sharedCategorizationFromCriminal(draft: CriminalWizardDraft): SharedCategorizationSeed {
  return {
    fileCategoryId: draft.fileCategoryId,
    fileCategory: draft.fileCategory,
    subFileCategoryId: draft.subFileCategoryId,
    subFileCategoryName: draft.subFileCategoryName,
    applicableAreaIds: draft.applicableAreaIds,
    applicableAreas: draft.applicableAreas,
    instituteType: draft.instituteType || 'government',
    corporateInstituteId: draft.corporateInstitute,
    corporateInstituteName: draft.corporateInstituteName || draft.corporateInstitute,
    corporateInstituteOther: draft.corporateInstituteOther,
    corporateInstituteContactName: draft.corporateInstituteContactName,
    corporateInstitutePrefix: draft.corporateInstitutePrefix,
    description: draft.description,
    provinceId: draft.provinceId,
    provinceName: draft.provinceName,
    districtId: draft.districtId,
    districtName: draft.districtName,
    yourReferenceNumber: draft.yourReferenceNumber,
    otherExternalReferences: draft.otherExternalReferences,
    caseNumber: draft.caseNumber,
    hasConnectedMatters: draft.hasConnectedMatters,
    connectedCaseNumber: draft.connectedCaseNumber,
    connectedAgdFileRef: draft.connectedAgdFileRef,
    urgencyLevelId: draft.urgencyLevelId,
    urgencyLevelName: draft.urgencyLevelName,
    deadline: draft.deadline,
    urgencyJustification: draft.urgencyJustification,
    deliveryMethodIds: draft.deliveryMethodIds,
    deliveryMethods: draft.deliveryMethods,
    deliveryOther: draft.deliveryOther,
    draftRequestId: draft.draftRequestId,
    referenceNo: draft.referenceNo,
  };
}

function sharedCategorizationFromCivilAdvise(draft: CivilAdviseWizardDraft): SharedCategorizationSeed {
  return {
    fileCategoryId: draft.fileCategoryId,
    fileCategory: draft.fileCategory,
    subFileCategoryId: draft.subFileCategoryId,
    subFileCategoryName: draft.subFileCategoryName,
    applicableAreaIds: draft.applicableAreaIds,
    applicableAreas: draft.applicableAreas,
    instituteType: draft.instituteType || 'government',
    corporateInstituteId: draft.corporateInstituteId,
    corporateInstituteName: draft.corporateInstituteName,
    corporateInstituteOther: draft.corporateInstituteOther,
    corporateInstituteContactName: draft.corporateInstituteContactName,
    corporateInstitutePrefix: draft.corporateInstitutePrefix,
    description: draft.description,
    provinceId: draft.provinceId,
    provinceName: draft.provinceName,
    districtId: draft.districtId,
    districtName: draft.districtName,
    yourReferenceNumber: draft.yourReferenceNumber,
    otherExternalReferences: draft.otherExternalReferences,
    caseNumber: draft.caseNumber,
    hasConnectedMatters: draft.hasConnectedMatters,
    connectedCaseNumber: draft.connectedCaseNumber,
    connectedAgdFileRef: draft.connectedAgdFileRef,
    urgencyLevelId: draft.urgencyLevelId,
    urgencyLevelName: draft.urgencyLevelName,
    deadline: draft.deadline,
    urgencyJustification: draft.urgencyJustification,
    deliveryMethodIds: draft.deliveryMethodIds,
    deliveryMethods: draft.deliveryMethods,
    deliveryOther: draft.deliveryOther,
    draftRequestId: draft.draftRequestId,
    referenceNo: draft.referenceNo,
  };
}

/** Shared categorization + intake fields when switching between category wizards. */
export function criminalToCivilAdviseSeed(draft: CriminalWizardDraft): Partial<CivilAdviseWizardDraft> {
  return {
    ...sharedCategorizationFromCriminal(draft),
    hasConnectedMatters: draft.hasConnectedMatters || 'no',
  };
}

export function civilAdviseToCriminalSeed(draft: CivilAdviseWizardDraft): Partial<CriminalWizardDraft> {
  const s = sharedCategorizationFromCivilAdvise(draft);
  return {
    ...s,
    corporateInstitute: s.corporateInstituteId,
  };
}

export function criminalToCivilLitigationSeed(draft: CriminalWizardDraft): Partial<CivilLitigationWizardDraft> {
  return {
    ...sharedCategorizationFromCriminal(draft),
    hasActionInstituted: defaultHasActionInstitutedForSubCategory(
      draft.subFileCategoryId,
      [],
      draft.subFileCategoryName,
    ),
    hasConnectedMatters: draft.hasConnectedMatters || 'no',
  };
}

export function civilAdviseToCivilLitigationSeed(draft: CivilAdviseWizardDraft): Partial<CivilLitigationWizardDraft> {
  return {
    ...sharedCategorizationFromCivilAdvise(draft),
    hasActionInstituted: defaultHasActionInstitutedForSubCategory(
      draft.subFileCategoryId,
      [],
      draft.subFileCategoryName,
    ),
    hasConnectedMatters: draft.hasConnectedMatters || 'no',
  };
}

export function civilLitigationToCriminalSeed(draft: CivilLitigationWizardDraft): Partial<CriminalWizardDraft> {
  const s = sharedCategorizationFromCivilLitigation(draft);
  return {
    ...s,
    corporateInstitute: s.corporateInstituteId,
  };
}

export function civilLitigationToCivilAdviseSeed(draft: CivilLitigationWizardDraft): Partial<CivilAdviseWizardDraft> {
  return {
    ...sharedCategorizationFromCivilLitigation(draft),
    hasConnectedMatters: draft.hasConnectedMatters || 'no',
  };
}

type SharedIntakeSeed = {
  description: string;
  provinceId: string;
  provinceName: string;
  districtId: string;
  districtName: string;
  yourReferenceNumber: string;
  otherExternalReferences: string;
  caseNumber: string;
  hasConnectedMatters: '' | 'yes' | 'no';
  connectedCaseNumber: string;
  connectedAgdFileRef: string;
  urgencyLevelId: string;
  urgencyLevelName: string;
  deadline: string;
  urgencyJustification: string;
  deliveryMethodIds: string[];
  deliveryMethods: string[];
  deliveryOther: string;
};

function sharedIntakeFromCriminal(draft: CriminalWizardDraft): SharedIntakeSeed {
  return {
    description: draft.description,
    provinceId: draft.provinceId,
    provinceName: draft.provinceName,
    districtId: draft.districtId,
    districtName: draft.districtName,
    yourReferenceNumber: draft.yourReferenceNumber,
    otherExternalReferences: draft.otherExternalReferences,
    caseNumber: draft.caseNumber,
    hasConnectedMatters: draft.hasConnectedMatters,
    connectedCaseNumber: draft.connectedCaseNumber,
    connectedAgdFileRef: draft.connectedAgdFileRef,
    urgencyLevelId: draft.urgencyLevelId,
    urgencyLevelName: draft.urgencyLevelName,
    deadline: draft.deadline,
    urgencyJustification: draft.urgencyJustification,
    deliveryMethodIds: draft.deliveryMethodIds,
    deliveryMethods: draft.deliveryMethods,
    deliveryOther: draft.deliveryOther,
  };
}

function sharedIntakeFromCivilAdvise(draft: CivilAdviseWizardDraft): SharedIntakeSeed {
  return {
    description: draft.description,
    provinceId: draft.provinceId,
    provinceName: draft.provinceName,
    districtId: draft.districtId,
    districtName: draft.districtName,
    yourReferenceNumber: draft.yourReferenceNumber,
    otherExternalReferences: draft.otherExternalReferences,
    caseNumber: draft.caseNumber,
    hasConnectedMatters: draft.hasConnectedMatters,
    connectedCaseNumber: draft.connectedCaseNumber,
    connectedAgdFileRef: draft.connectedAgdFileRef,
    urgencyLevelId: draft.urgencyLevelId,
    urgencyLevelName: draft.urgencyLevelName,
    deadline: draft.deadline,
    urgencyJustification: draft.urgencyJustification,
    deliveryMethodIds: draft.deliveryMethodIds,
    deliveryMethods: draft.deliveryMethods,
    deliveryOther: draft.deliveryOther,
  };
}

function sharedIntakeFromCivilLitigation(draft: CivilLitigationWizardDraft): SharedIntakeSeed {
  return {
    description: draft.description,
    provinceId: draft.provinceId,
    provinceName: draft.provinceName,
    districtId: draft.districtId,
    districtName: draft.districtName,
    yourReferenceNumber: draft.yourReferenceNumber,
    otherExternalReferences: draft.otherExternalReferences,
    caseNumber: draft.caseNumber,
    hasConnectedMatters: draft.hasConnectedMatters,
    connectedCaseNumber: draft.connectedCaseNumber,
    connectedAgdFileRef: draft.connectedAgdFileRef,
    urgencyLevelId: draft.urgencyLevelId,
    urgencyLevelName: draft.urgencyLevelName,
    deadline: draft.deadline,
    urgencyJustification: draft.urgencyJustification,
    deliveryMethodIds: draft.deliveryMethodIds,
    deliveryMethods: draft.deliveryMethods,
    deliveryOther: draft.deliveryOther,
  };
}

export function criminalToExtraditionMlaSeed(draft: CriminalWizardDraft): Partial<ExtraditionMlaWizardDraft> {
  return {
    ...sharedCategorizationFromCriminal(draft),
    ...sharedIntakeFromCriminal(draft),
    hasConnectedMatters: draft.hasConnectedMatters || 'no',
  };
}

export function civilAdviseToExtraditionMlaSeed(draft: CivilAdviseWizardDraft): Partial<ExtraditionMlaWizardDraft> {
  return {
    ...sharedCategorizationFromCivilAdvise(draft),
    ...sharedIntakeFromCivilAdvise(draft),
    hasConnectedMatters: draft.hasConnectedMatters || 'no',
  };
}

export function civilLitigationToExtraditionMlaSeed(
  draft: CivilLitigationWizardDraft,
): Partial<ExtraditionMlaWizardDraft> {
  return {
    ...sharedCategorizationFromCivilLitigation(draft),
    ...sharedIntakeFromCivilLitigation(draft),
    hasConnectedMatters: draft.hasConnectedMatters || 'no',
  };
}

function sharedIntakeFromCivilAppellate(draft: CivilAppellateWizardDraft): SharedIntakeSeed {
  return {
    description: draft.description,
    provinceId: draft.provinceId,
    provinceName: draft.provinceName,
    districtId: draft.districtId,
    districtName: draft.districtName,
    yourReferenceNumber: draft.yourReferenceNumber,
    otherExternalReferences: draft.otherExternalReferences,
    caseNumber: draft.caseNumber,
    hasConnectedMatters: draft.hasConnectedMatters,
    connectedCaseNumber: draft.connectedCaseNumber,
    connectedAgdFileRef: draft.connectedAgdFileRef,
    urgencyLevelId: draft.urgencyLevelId,
    urgencyLevelName: draft.urgencyLevelName,
    deadline: draft.deadline,
    urgencyJustification: draft.urgencyJustification,
    deliveryMethodIds: draft.deliveryMethodIds,
    deliveryMethods: draft.deliveryMethods,
    deliveryOther: draft.deliveryOther,
  };
}

export function civilAppellateToExtraditionMlaSeed(
  draft: CivilAppellateWizardDraft,
): Partial<ExtraditionMlaWizardDraft> {
  return {
    ...sharedCategorizationFromCivilAppellate(draft),
    ...sharedIntakeFromCivilAppellate(draft),
    hasConnectedMatters: draft.hasConnectedMatters || 'no',
  };
}

export function civilLitigationToCivilAppellateSeed(
  draft: CivilLitigationWizardDraft,
): Partial<CivilAppellateWizardDraft> {
  return {
    ...sharedCategorizationFromCivilLitigation(draft),
    hasConnectedMatters: draft.hasConnectedMatters || 'no',
    plaintiffName: draft.plaintiffName,
    defendantName: draft.defendantName,
    isIncorporatedBody: draft.involvesPublicCorporation === 'yes',
    otherInvolvedInstitutions: draft.otherInvolvedInstitutions,
    legalOfficerName: draft.legalOfficerName,
    legalOfficerContact: draft.legalOfficerContact,
  };
}

export function criminalToCivilAppellateSeed(draft: CriminalWizardDraft): Partial<CivilAppellateWizardDraft> {
  return {
    ...sharedCategorizationFromCriminal(draft),
    hasConnectedMatters: draft.hasConnectedMatters || 'no',
  };
}


export function civilAdviseToCivilAppellateSeed(draft: CivilAdviseWizardDraft): Partial<CivilAppellateWizardDraft> {
  return {
    ...sharedCategorizationFromCivilAdvise(draft),
    hasConnectedMatters: draft.hasConnectedMatters || 'no',
  };
}

export function civilAppellateToCivilLitigationSeed(
  draft: CivilAppellateWizardDraft,
): Partial<CivilLitigationWizardDraft> {
  return {
    ...sharedCategorizationFromCivilAppellate(draft),
    hasConnectedMatters: draft.hasConnectedMatters || 'no',
    plaintiffName: draft.plaintiffName,
    defendantName: draft.defendantName,
    involvesGovernmentBody: draft.isIncorporatedBody ? 'no' : 'yes',
    involvesPublicCorporation: draft.isIncorporatedBody ? 'yes' : 'no',
    otherInvolvedInstitutions: draft.otherInvolvedInstitutions,
    legalOfficerName: draft.legalOfficerName,
    legalOfficerContact: draft.legalOfficerContact,
  };
}

export function civilAppellateToCivilAdviseSeed(draft: CivilAppellateWizardDraft): Partial<CivilAdviseWizardDraft> {
  return {
    ...sharedCategorizationFromCivilAppellate(draft),
    hasConnectedMatters: draft.hasConnectedMatters || 'no',
  };
}

export function civilAppellateToCriminalSeed(draft: CivilAppellateWizardDraft): Partial<CriminalWizardDraft> {
  const s = sharedCategorizationFromCivilAppellate(draft);
  return {
    ...s,
    corporateInstitute: s.corporateInstituteId,
  };
}

function sharedCategorizationFromCivilAppellate(draft: CivilAppellateWizardDraft): SharedCategorizationSeed {
  return {
    fileCategoryId: draft.fileCategoryId,
    fileCategory: draft.fileCategory,
    subFileCategoryId: draft.subFileCategoryId,
    subFileCategoryName: draft.subFileCategoryName,
    applicableAreaIds: draft.applicableAreaIds,
    applicableAreas: draft.applicableAreas,
    instituteType: draft.instituteType || 'government',
    corporateInstituteId: draft.corporateInstituteId,
    corporateInstituteName: draft.corporateInstituteName,
    corporateInstituteOther: draft.corporateInstituteOther,
    corporateInstituteContactName: draft.corporateInstituteContactName,
    corporateInstitutePrefix: draft.corporateInstitutePrefix,
    description: draft.description,
    provinceId: draft.provinceId,
    provinceName: draft.provinceName,
    districtId: draft.districtId,
    districtName: draft.districtName,
    yourReferenceNumber: draft.yourReferenceNumber,
    otherExternalReferences: draft.otherExternalReferences,
    caseNumber: draft.caseNumber,
    hasConnectedMatters: draft.hasConnectedMatters,
    connectedCaseNumber: draft.connectedCaseNumber,
    connectedAgdFileRef: draft.connectedAgdFileRef,
    urgencyLevelId: draft.urgencyLevelId,
    urgencyLevelName: draft.urgencyLevelName,
    deadline: draft.deadline,
    urgencyJustification: draft.urgencyJustification,
    deliveryMethodIds: draft.deliveryMethodIds,
    deliveryMethods: draft.deliveryMethods,
    deliveryOther: draft.deliveryOther,
    draftRequestId: draft.draftRequestId,
    referenceNo: draft.referenceNo,
  };
}


function sharedCategorizationFromCivilLitigation(draft: CivilLitigationWizardDraft): SharedCategorizationSeed {
  return {
    fileCategoryId: draft.fileCategoryId,
    fileCategory: draft.fileCategory,
    subFileCategoryId: draft.subFileCategoryId,
    subFileCategoryName: draft.subFileCategoryName,
    applicableAreaIds: draft.applicableAreaIds,
    applicableAreas: draft.applicableAreas,
    instituteType: draft.instituteType || 'government',
    corporateInstituteId: draft.corporateInstituteId,
    corporateInstituteName: draft.corporateInstituteName,
    corporateInstituteOther: draft.corporateInstituteOther,
    corporateInstituteContactName: draft.corporateInstituteContactName,
    corporateInstitutePrefix: draft.corporateInstitutePrefix,
    description: draft.description,
    provinceId: draft.provinceId,
    provinceName: draft.provinceName,
    districtId: draft.districtId,
    districtName: draft.districtName,
    yourReferenceNumber: draft.yourReferenceNumber,
    otherExternalReferences: draft.otherExternalReferences,
    caseNumber: draft.caseNumber,
    hasConnectedMatters: draft.hasConnectedMatters,
    connectedCaseNumber: draft.connectedCaseNumber,
    connectedAgdFileRef: draft.connectedAgdFileRef,
    urgencyLevelId: draft.urgencyLevelId,
    urgencyLevelName: draft.urgencyLevelName,
    deadline: draft.deadline,
    urgencyJustification: draft.urgencyJustification,
    deliveryMethodIds: draft.deliveryMethodIds,
    deliveryMethods: draft.deliveryMethods,
    deliveryOther: draft.deliveryOther,
    draftRequestId: draft.draftRequestId,
    referenceNo: draft.referenceNo,
  };
}
